import { useEffect, useRef, useState } from "react";
import { api } from "../../services/apiService";
import { audioModeAvailable } from "./audio-audience";
import "./audio-listen-panel.scss";

/**
 * Flag-gated dark "Listen" panel for Audio Mode ("Airpod Learning", QLearn-service
 * docs/qlearn-audio-mode-design.md). Off by default: renders null unless
 * REACT_APP_AUDIO_MODE_ENABLED === "true", so the surface is byte-identical to today.
 *
 * Layered UNDER that master flag is REACT_APP_AUDIO_MODE_AUDIENCE (off|tutors|all,
 * case-insensitive). DELIBERATE divergence from the more-like-this panel's
 * audience parse: there ABSENT = "all" (an already-shipped surface had to stay
 * byte-identical); audio is a NEW surface, so ABSENT (and any unrecognized value)
 * = "off", mirroring the server's fail-closed qlearn.audio.audience parse. This is
 * UX-only — the server audience gate stays authoritative and 404s non-tutors.
 *
 * Per question it asks the dark manifest endpoint; a 404 (no audio / server gate
 * off / caller outside the server audience) renders NOTHING — audio never blocks
 * or alters quiz-taking (design §7 fail-open rule). With a manifest, it renders
 * big tap targets per segment (phone-first). Answer-revealing segments
 * (answerBrief / deeper / the stitched full track) are offered only in review —
 * pre-answer playback exposes stem + options alone, so the panel cannot leak the
 * answer mid-question.
 *
 * Playback fetches segment bytes through the auth-patched global fetch into an
 * object URL (the getReviewItemImage precedent) — a plain <audio src> could not
 * carry the Bearer token the gated proxy endpoint requires.
 */
const SEGMENT_LABELS = {
  stem: "Stem",
  options: "Options",
  answerBrief: "Answer",
  deeper: "Deeper",
  listen: "Listen (full)"
};

/** Segments a caller may hear BEFORE answering — never the answer-revealing ones. */
const PRE_ANSWER_SEGMENTS = ["stem", "options"];

export default function AudioListenPanel({ questionId, isReview = false }) {
  const [manifest, setManifest] = useState(null);
  const [playingSegment, setPlayingSegment] = useState("");
  const [error, setError] = useState("");
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);

  const mounted = audioModeAvailable() && !!questionId;

  useEffect(() => {
    if (!mounted) {
      return undefined;
    }
    let cancelled = false;
    setManifest(null);
    setPlayingSegment("");
    setError("");
    api.getAudioManifest(questionId).then((res) => {
      if (cancelled) {
        return;
      }
      // doGetCall contract: 200 resolves to the body; any non-200 to {status,
      // errMsg}; a network drop to an Error. A 404 = no audio for this question
      // (or the server gate is dark/denying) — render nothing, never an error.
      const ok = res && !Array.isArray(res) && Array.isArray(res.segments);
      setManifest(ok ? res : null);
    });
    return () => {
      cancelled = true;
      // Drop any in-flight object URL when the question changes.
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [mounted, questionId]);

  if (!mounted || !manifest) {
    return null;
  }

  const visibleSegments = manifest.segments.filter(
    (s) => s && SEGMENT_LABELS[s.segment] && (isReview || PRE_ANSWER_SEGMENTS.includes(s.segment))
  );
  if (!visibleSegments.length) {
    return null;
  }

  const play = async (segment) => {
    setError("");
    setPlayingSegment(segment.segment);
    try {
      const blob = await api.getAudioSegmentBlob(segment.url);
      if (!(blob instanceof Blob)) {
        throw new Error(`segment request failed: ${blob && blob.status}`);
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      objectUrlRef.current = URL.createObjectURL(blob);
      const el = audioRef.current;
      if (el) {
        el.src = objectUrlRef.current;
        await el.play();
      }
    } catch (e) {
      // Fail-open rule: a broken/missing track never blocks quiz-taking.
      setPlayingSegment("");
      setError("Audio isn't available right now.");
    }
  };

  return (
    <div className="audio-listen-panel" data-testid="audio-listen-panel">
      <div className="audio-listen-panel__title">Listen</div>
      <div className="audio-listen-panel__buttons">
        {visibleSegments.map((segment) => (
          <button
            key={segment.segment}
            type="button"
            className={`audio-listen-panel__button${
              playingSegment === segment.segment ? " audio-listen-panel__button--active" : ""
            }`}
            data-testid={`audio-play-${segment.segment}`}
            onClick={() => play(segment)}
          >
            ▶ {SEGMENT_LABELS[segment.segment]}
          </button>
        ))}
      </div>
      {error && (
        <div className="audio-listen-panel__error" data-testid="audio-listen-error">
          {error}
        </div>
      )}
      {/* Single element reused across segments; controls shown once something plays. */}
      <audio
        ref={audioRef}
        data-testid="audio-listen-element"
        controls={!!playingSegment}
        onEnded={() => setPlayingSegment("")}
      />
    </div>
  );
}
